import React, { useState, useEffect } from 'react'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { Alert, Button } from '@mui/material'
import { FaRegTrashCan, FaArrowRotateRight } from 'react-icons/fa6'
import { FaEdit } from 'react-icons/fa'
import { observer } from 'mobx-react-lite'
import imageCompression from 'browser-image-compression'
import { v4 as uuidv4 } from 'uuid'
import { ReactPhotoEditor } from 'react-photo-editor'
import { config } from '../config'
import logger from '../utils/logger'
import apiClient from '../utils/apiClient'
import objectStore from '../stores/objectStore'

// Добавление стилей для мобильной адаптации редактора
const mobileEditorStyles = document.createElement('style');
mobileEditorStyles.textContent = `
	/* Стили для адаптации редактора на мобильных устройствах */
	@media (max-width: 768px) {
		/* Обеспечиваем, чтобы контент не выходил за пределы модального окна */
		.rpe-modal-content {
			max-width: 100% !important;
			overflow: hidden !important;
		}
		
		/* Уменьшаем размер элементов управления */
		.rpe-editor-controls button,
		.rpe-editor-actions button {
			padding: 0.4rem !important;
			font-size: 0.9rem !important;
		}
		
		/* Оптимизируем отображение слайдеров контроля */
		.rpe-slider-container {
			margin: 0.5rem 0 !important;
		}
		
		/* Улучшенные стили для холста */
		.rpe-canvas-container {
			overflow: hidden !important;
            max-height: 60vh !important;
            display: flex !important;
            justify-content: center !important;
            align-items: center !important;
		}
		
		.rpe-canvas {
			max-height: 55vh !important;
            max-width: 90vw !important;
            width: auto !important;
            height: auto !important;
            object-fit: contain !important;
		}
		
		/* Стиль для основного контейнера редактора */
		.rpe-editor-container {
            max-height: 90vh !important;
            display: flex !important;
            flex-direction: column !important;
        }
        
        /* Убедимся, что элементы управления всегда видны */
        .rpe-controls-container {
            flex-shrink: 0 !important;
        }
	}
`;
document.head.appendChild(mobileEditorStyles);

const FileUpload = observer(({ title, idNewObject }) => {
	const [files, setFiles] = useState([])
	const [editingFile, setEditingFile] = useState(null)
	const [showEditor, setShowEditor] = useState(false)
	const [uploadError, setUploadError] = useState('')
	const [editorDimensions, setEditorDimensions] = useState({
		width: '50rem',
		height: '40rem'
	})

	// Обработка изменения размера окна для адаптивности редактора
	useEffect(() => {
		const updateDimensions = () => {
			// Проверяем ширину окна и устанавливаем соответствующие размеры
			if (window.innerWidth < 768) { // мобильные устройства
				setEditorDimensions({
					width: '95vw',
					height: '80vh'
				})
			} else if (window.innerWidth < 1024) { // планшеты
				setEditorDimensions({
					width: '90vw',
					height: '70vh'
				})
			} else { // десктоп
				setEditorDimensions({
					width: '50rem',
					height: '40rem'
				})
			}
		}

		// Устанавливаем начальные размеры
		updateDimensions()

		// Добавляем обработчик изменения размера окна
		window.addEventListener('resize', updateDimensions)

		// Убираем обработчик при размонтировании компонента
		return () => {
			window.removeEventListener('resize', updateDimensions)
		}
	}, [])

	// Настройки для сжатия изображений
	const compressionOptions = {
		maxSizeMB: 1, // Максимальный размер файла (в мегабайтах)
		maxWidthOrHeight: 840, // Максимальная ширина или высота изображения
		useWebWorker: true, // Использовать Web Worker для ускорения
	}

	// Асинхронная функция для загрузки файла на сервер
	const uploadFileToServer = async (file, sort, uuid) => {
		logger.log('🀄 uploadFileToServer')

		const formData = new FormData()
		formData.append('idNewObject', idNewObject) // ID объекта
		formData.append('tableName', title.tableName) // Имя таблицы
		formData.append('sort', sort) // Порядок сортировки
		formData.append('file', file.file) // Физический файл
		formData.append('uuid', uuid) // UUID

		logger.log('---------idNewObject:', idNewObject)

		for (let pair of formData.entries()) {
			logger.log(`${pair[0]}:`, pair[1])
		}

		logger.log('🀄 ENDP-3399024')
		const response = await apiClient.post('/upload-file', formData, {
			headers: {
				'Content-Type': 'multipart/form-data'
			}
		})

		// Обновляем объект в objectStore после успешной загрузки
		const { file: fileInfo } = response.data
		if (!fileInfo?.imageUrl) {
			throw new Error('Server did not return an image URL')
		}
			
		// Добавляем фото в объект в хранилище
		if (objectStore.objects.length > 0) {
			const targetObject = objectStore.objects.find(obj => obj.id === parseInt(idNewObject))
				
			if (targetObject) {
				// Инициализируем массивы, если их еще нет
				if (!targetObject.photos) targetObject.photos = []
				if (!targetObject.layoutPhotos) targetObject.layoutPhotos = []

				// Добавляем фото в соответствующий массив в зависимости от типа
				if (title.tableName === '1') {
					targetObject.photos.push(fileInfo)
				} else if (title.tableName === '2') {
					targetObject.layoutPhotos.push(fileInfo)
				}

				logger.log('✅ Объект в хранилище обновлен с новым фото:', fileInfo)
			}
		}

		return fileInfo
	}

	// Добавление новых файлов
	const handleFileUpload = async event => {
		logger.log('🀄 handleFileUpload')
		const newFiles = Array.from(event.target.files)
		setUploadError('')

		if (files.length + newFiles.length > 50) {
			alert('50 файлов максимум')
			return
		}

		const uploadResults = await Promise.allSettled(
			newFiles.map(async (file, index) => {
				// Сжимаем изображение
				const compressedFile = await imageCompression(
					file,
					compressionOptions
				)

				logger.log(
					`Оригинальный размер: ${(file.size / 1024 / 1024).toFixed(2)} MB`
				)
				logger.log(
					`Сжатый размер: ${(compressedFile.size / 1024 / 1024).toFixed(2)} MB`
				)

				// Создаём объект файла с сохранением оригинального расширения
				const compressedFileWithExtension = new File(
					[compressedFile],
					`${file.name}`,
					{ type: file.type }
				)

				const sort = files.length + index + 1
				const uuid = uuidv4()

				const fileInfo = await uploadFileToServer(
					{ file: compressedFileWithExtension },
					sort,
					uuid
				)

				return {
					id: `${file.name}-${Date.now()}`,
					file: compressedFileWithExtension,
					preview: fileInfo.imageUrl,
					rotation: 0,
					sort,
					path: fileInfo.objectKey,
					uuid,
					serverId: fileInfo.id,
				}
			})
		)

		const uploadedFiles = uploadResults
			.filter(result => result.status === 'fulfilled')
			.map(result => result.value)
		const failedUploads = uploadResults.filter(result => result.status === 'rejected')

		failedUploads.forEach(result => logger.error('Ошибка загрузки файла:', result.reason))
		if (failedUploads.length > 0) {
			setUploadError(
				failedUploads.length === 1
					? 'Не удалось загрузить фотографию. Попробуйте ещё раз.'
					: `Не удалось загрузить фотографии: ${failedUploads.length}. Попробуйте ещё раз.`
			)
		}

		if (uploadedFiles.length > 0) {
			setFiles(prevFiles => [...prevFiles, ...uploadedFiles])
		}
		event.target.value = ''
	}

	// Перетаскивание файлов
	const handleDragEnd = async result => {
		logger.log('🀄 handleDragEnd')
		if (!result.destination) return

		const reorderedFiles = Array.from(files)
		const [movedItem] = reorderedFiles.splice(result.source.index, 1)
		reorderedFiles.splice(result.destination.index, 0, movedItem)

		// Обновляем порядок `sort`
		const updatedFiles = reorderedFiles.map((file, index) => ({
			...file,
			sort: index + 1,
		}))

		setFiles(updatedFiles)

		// Отправляем обновленный порядок сортировки на сервер
		try {
			logger.log('Отправляем обновленный порядок сортировки на сервер')

			await apiClient.post('/update-sort-files', {
				idNewObject,
				tableName: title.tableName,
				files: updatedFiles
			})
			
			// Обновляем порядок сортировки в объекте хранилища
			if (objectStore.objects.length > 0) {
				// Находим объект в хранилище
				const targetObject = objectStore.objects.find(obj => obj.id === parseInt(idNewObject))
				
				if (targetObject) {
					// Определяем, какой массив обновлять (photos или layoutPhotos)
					if (title.tableName === '1' && targetObject.photos) {
						// Обновляем порядок сортировки в массиве photos
						// Копируем существующие фото и сортируем их по порядку из updatedFiles
						const updatedPhotos = [...targetObject.photos]
						updatedPhotos.sort((a, b) => {
							const fileA = updatedFiles.find(f => f.uuid === a.uuid)
							const fileB = updatedFiles.find(f => f.uuid === b.uuid)
							return (fileA?.sort || 0) - (fileB?.sort || 0)
						})
						
						// Обновляем значения sortOrder
						updatedPhotos.forEach((photo, index) => {
							photo.sortOrder = index + 1
						})
						
						targetObject.photos = updatedPhotos
						logger.log('✅ Порядок сортировки фото обновлен в объекте хранилища')
					} else if (title.tableName === '2' && targetObject.layoutPhotos) {
						// Обновляем порядок сортировки в массиве layoutPhotos
						const updatedLayoutPhotos = [...targetObject.layoutPhotos]
						updatedLayoutPhotos.sort((a, b) => {
							const fileA = updatedFiles.find(f => f.uuid === a.uuid)
							const fileB = updatedFiles.find(f => f.uuid === b.uuid)
							return (fileA?.sort || 0) - (fileB?.sort || 0)
						})
						
						updatedLayoutPhotos.forEach((photo, index) => {
							photo.sortOrder = index + 1
						})
						
						targetObject.layoutPhotos = updatedLayoutPhotos
						logger.log('✅ Порядок сортировки планировок обновлен в объекте хранилища')
					}
				}
			}
		} catch (error) {
			logger.error('Ошибка обновления сортировки:', error)
		}
	}

	// Удаление файла
	const handleRemove = async uuid => {
		logger.log('🀄 handleRemove')
		const fileToRemove = files.find(file => file.uuid === uuid)
		
		logger.log('fileToRemove:', fileToRemove)

		try {
			await apiClient.delete(`/delete-object/${uuid}`, {
				data: {
					tableName: title.tableName,
					idNewObject,
				}
			})

			// Удаляем файл из состояния
			const updatedFiles = files
				.filter(file => file.uuid !== uuid)
				.map((file, index) => ({
					...file,
					sort: index + 1, // Обновляем порядок сортировки
				}))

			setFiles(updatedFiles)
			
			// Удаляем фото из объекта в хранилище
			if (objectStore.objects.length > 0) {
				// Находим объект в хранилище
				const targetObject = objectStore.objects.find(obj => obj.id === parseInt(idNewObject))
				
				if (targetObject) {
					// Определяем, из какого массива удалять фото (photos или layoutPhotos)
					if (title.tableName === '1' && targetObject.photos) {
						// Удаляем фото из массива photos по uuid
						targetObject.photos = targetObject.photos.filter(photo => photo.uuid !== uuid)
						logger.log('✅ Фото удалено из photos в объекте хранилища')
					} else if (title.tableName === '2' && targetObject.layoutPhotos) {
						// Удаляем фото из массива layoutPhotos по uuid
						targetObject.layoutPhotos = targetObject.layoutPhotos.filter(photo => photo.uuid !== uuid)
						logger.log('✅ Фото удалено из layoutPhotos в объекте хранилища')
					}
				}
			}
		} catch (error) {
			logger.error('Ошибка удаления файла:', error)
		}
	}

	// Поворот изображения
	const handleRotate = async id => {
		logger.log('🀄 handleRotate')

		// Находим файл, который нужно повернуть
		const fileToRotate = files.find(file => file.id === id)
		if (!fileToRotate) {
			logger.error('Файл с указанным ID не найден')
			return
		}

		// Отправляем запрос на сервер
		try {
			const response = await apiClient.post(`/rotate-photo/${fileToRotate.uuid}`, {
				tableName: title.tableName
			})

			logger.log('Фотография успешно повёрнута')

			// Если сервер вернул успешный ответ, обновляем состояние на фронте
			setFiles(prevFiles =>
				prevFiles.map(file =>
					file.id === id ? { ...file, rotation: file.rotation + 90 } : file
				)
			)
		} catch (error) {
			logger.error('Ошибка отправки запроса на сервер:', error)
		}
	}

	// Редактирование файла
	const handleEdit = file => {
		logger.log('🀄 handleEdit')
		setEditingFile(file)
		setShowEditor(true)
	}

	// Обработка завершения редактирования
	const handleEditDone = async (editedImage) => {
		logger.log('🀄 handleEditDone')
		if (editedImage) {
			try {
				// Отправляем обновленный файл на сервер
				logger.log('Отправляем обновленный файл на сервер')
				
				const formData = new FormData()
				formData.append('idNewObject', idNewObject)
				formData.append('tableName', title.tableName)
				formData.append('file', editedImage)
				formData.append('uuid', editingFile.uuid)
				formData.append('sort', editingFile.sort)

				const response = await apiClient.post('/upload-edited-file', formData, {
					headers: {
						'Content-Type': 'multipart/form-data'
					}
				})
				const imageUrl = response.data?.file?.imageUrl
				if (!imageUrl) {
					throw new Error('Server did not return an image URL')
				}

				const updatedFile = {
					...editingFile,
					file: editedImage,
					preview: imageUrl
				}

				setFiles(prevFiles =>
					prevFiles.map(file =>
						file.id === editingFile.id ? updatedFile : file
					)
				)

				// Обновляем файл в объекте хранилища
				if (objectStore.objects.length > 0) {
					// Находим объект в хранилище
					const targetObject = objectStore.objects.find(obj => obj.id === parseInt(idNewObject))
					
					if (targetObject) {
						// Определяем, из какого массива обновлять файл (photos или layoutPhotos)
						if (title.tableName === '1' && targetObject.photos) {
							// Обновляем файл в массиве photos
							targetObject.photos = targetObject.photos.map(photo =>
								photo.uuid === editingFile.uuid ? {
									...photo,
									imageUrl
								} : photo
							)
							logger.log('✅ Файл обновлен в photos в объекте хранилища')
						} else if (title.tableName === '2' && targetObject.layoutPhotos) {
							// Обновляем файл в массиве layoutPhotos
							targetObject.layoutPhotos = targetObject.layoutPhotos.map(photo =>
								photo.uuid === editingFile.uuid ? {
									...photo,
									imageUrl
								} : photo
							)
							logger.log('✅ Файл обновлен в layoutPhotos в объекте хранилища')
						}
					}
				}
			} catch (error) {
				logger.error('Ошибка обновления файла:', error)
				setUploadError('Не удалось сохранить изменения фотографии. Попробуйте ещё раз.')
			}
		}
		setShowEditor(false)
		setEditingFile(null)
	}

	function getMe() {
		logger.log(`🎢 files:`, files)
		logger.log(`🎢 title:`, title)
		logger.log(`🎢 idNewObject:`, idNewObject)
	}

	return (
		<div className='mt-14 border border-slate-400 p-4 border-dashed rounded-md'>
	
			<h3>{title.label}</h3>
			<div className='mt-3'>
				<Button variant='contained' component='label' color='inherit'>
					Добавить фото
					<input
						type='file'
						multiple
						hidden
						onChange={handleFileUpload}
						accept='.jpg,.jpeg,.png' // Разрешены только JPG, JPEG и PNG'
					/>
				</Button>
			</div>
			{uploadError && (
				<Alert severity='error' sx={{ mt: 2 }} onClose={() => setUploadError('')}>
					{uploadError}
				</Alert>
			)}
			<DragDropContext onDragEnd={handleDragEnd}>
				<Droppable droppableId='files' direction='horizontal'>
					{provided => (
						<div
							{...provided.droppableProps}
							ref={provided.innerRef}
							style={{
								display: 'flex',
								flexWrap: 'wrap',
								marginTop: '20px',
								gap: '10px',
							}}
						>
							{files.map((file, index) => (
								<Draggable
									key={file.uuid}
									draggableId={file.uuid}
									index={index}
								>
									{provided => (
										<div
											ref={provided.innerRef}
											{...provided.draggableProps}
											{...provided.dragHandleProps}
											style={{
												position: 'relative',
												width: '150px',
												height: '150px',
												...provided.draggableProps.style,
											}}
											className='mb-5'
										>
											<img
												src={file.preview}
												alt={file.file.name}
												onError={() => setUploadError('Загруженная фотография недоступна. Обновите страницу или повторите загрузку.')}
												style={{
													width: '100%',
													height: '100%',
													objectFit: 'cover',
													borderRadius: '5px',
													transform: `rotate(${file.rotation}deg)`,
												}}
											/>
											<div
												style={{
													display: 'flex',
													justifyContent: 'space-between',
													marginTop: '5px',
												}}
											>
												<button
													onClick={() => handleRotate(file.id)}
													style={{ cursor: 'pointer', marginRight: '10px' }}
												>
													<FaArrowRotateRight />
												</button>
												<button
													onClick={() => handleEdit(file)}
													style={{ cursor: 'pointer', marginRight: '10px' }}
												>
													<FaEdit />
												</button>
												<button
													onClick={() => handleRemove(file.uuid)}
													style={{ cursor: 'pointer' }}
												>
													<FaRegTrashCan />
												</button>
											</div>
										</div>
									)}
								</Draggable>
							))}
							{provided.placeholder}
						</div>
					)}
				</Droppable>
			</DragDropContext>

			{/* Компонент редактора */} 
			{showEditor && editingFile && (
				<>
					<ReactPhotoEditor 
						file={editingFile.file}
						open={showEditor}
						onClose={() => setShowEditor(false)}
						onSaveImage={handleEditDone}
						downloadOnSave={false}
						modalWidth={editorDimensions.width}
						modalHeight={editorDimensions.height}
					/>
				</>
			)}
		</div>
	)
})

export default FileUpload
